import { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { readImpersonationState, endImpersonation } from '@/lib/impersonation';
import { useAuth } from '@/hooks/useAuth';

/**
 * Always-available exit from an impersonated session (#2836).
 *
 * The only "stop impersonating" control used to live in the internal sidebar, so impersonating
 * a user whose shell is the buyer UI left no way out — and that shell's Sign Out button
 * destroyed the stashed admin session, stranding the operator at /auth.
 *
 * Mounted above DomainGate in App.tsx so it survives the "Access Denied" screen too: impersonate
 * a user who isn't on the current custom domain's brand and that screen replaces the whole app,
 * which would otherwise be another dead end.
 *
 * ## Why a bottom-centre pill rather than the conventional full-width top bar
 * Two constraints rule out the usual banner:
 *   - It cannot be in normal flow. Several shells pin to `h-svh`/`min-h-svh`; adding height above
 *     them desyncs the fixed sidebar from its inset — regression #2709.
 *   - A `fixed` full-width bar at top:0 then *covers* the `h-16` app header, and with it the
 *     `SidebarTrigger` at top-left. On mobile that trigger is the only way to open the nav, so
 *     the operator could exit but not actually look around the account being impersonated.
 * Bottom centre is clear of the header, clear of the sidebar rail, and clear of the softphone
 * (bottom-right). z-index is z-[45], not z-40: several bottom-docked bars (listing SummaryRail,
 * ListingSellerCTA, TriageBulkBar) are also z-40 and sit LATER in the DOM, so an equal z-index
 * loses the tie and hides the only exit control. 45 clears them and stays under the install
 * prompt (z-50) and toaster.
 */
export function ImpersonationBar() {
  const { user } = useAuth();
  const [exiting, setExiting] = useState(false);
  // sessionStorage is not reactive and not observable, so read it on EVERY render and use a
  // tick purely to schedule re-renders. Snapshotting once in useState looked tidier but meant a
  // stash written after mount never surfaced — and that is a real path: the e2e impersonation
  // helper (and any in-page session swap) sets the key without a navigation, relying on the
  // SIGNED_IN re-render to reveal the bar. Listeners are installed unconditionally for the same
  // reason: gating them on "currently impersonating" can never detect the transition INTO it.
  const [, setTick] = useState(0);
  useEffect(() => {
    const refresh = () => setTick((t) => t + 1);
    window.addEventListener('focus', refresh);
    // Also catches the 1-hour window lapsing while the tab sits open.
    const timer = window.setInterval(refresh, 30_000);
    return () => {
      window.removeEventListener('focus', refresh);
      window.clearInterval(timer);
    };
  }, []);

  const state = readImpersonationState();

  // Guard the double-click: endImpersonation clears the stash before awaiting setSession, so a
  // second click lands in its "no stash" branch and signs out for real — re-creating the exact
  // stranding this fixes.
  const handleExit = useCallback(() => {
    if (exiting) return;
    setExiting(true);
    void endImpersonation();
  }, [exiting]);

  if (!state.isImpersonating) return null;

  const label = user?.email || state.originalAdmin?.email || 'another user';

  return (
    <div
      role="region"
      aria-label="Impersonation session"
      className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 z-[45] flex -translate-x-1/2 items-center gap-3 rounded-full border border-amber-600/40 bg-amber-500/95 py-2 pl-4 pr-2 shadow-lg backdrop-blur max-w-[calc(100vw-2rem)]"
    >
      <ShieldAlert className="h-4 w-4 shrink-0 text-amber-950" aria-hidden="true" />
      <p className="min-w-0 truncate text-xs font-medium text-amber-950 sm:text-sm">
        {state.isExpired ? (
          <>Impersonation expired — <span className="font-semibold">{label}</span></>
        ) : (
          <>Viewing as <span className="font-semibold">{label}</span></>
        )}
      </p>
      <Button
        size="sm"
        variant="outline"
        onClick={handleExit}
        disabled={exiting}
        className="h-8 shrink-0 rounded-full border-amber-900/30 bg-white/90 text-amber-950 hover:bg-white min-h-[44px] sm:min-h-0"
      >
        {exiting ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : 'Return to admin'}
      </Button>
    </div>
  );
}
